import type {
  AbcXyzReportDto,
  AdminProductDto,
  ApproveClientInput,
  AssignSpotInput,
  AuditEntryDto,
  AuditQuery,
  BulkCreatedClient,
  CatalogCountsDto,
  CategoryDto,
  CellsListDto,
  ChangeCredentialsInput,
  CreateCellInput,
  ClientDto,
  ClientsQuery,
  ConnectionTestDto,
  CounterpartyDto,
  CreateStaffInput,
  DailyOrdersReportDto,
  DeadStockReportDto,
  DeliveryDto,
  DeliveryStatus,
  ExpiryReportDto,
  InventoryReportDto,
  LoginResponse,
  MoyskladOptionsDto,
  MoyskladSettingsDto,
  NotificationSettingsDto,
  NotificationSettingsUpdate,
  PickCategoriesDto,
  MoyskladWebhooksStatusDto,
  PosBridgeConfigUpdate,
  PosBridgeSecretResultDto,
  PosBridgeSettingsDto,
  PosOutboxRowDto,
  OrderDto,
  OrdersQuery,
  Paginated,
  PriceTypeDto,
  ProductCellDto,
  ProductsQuery,
  ProfitReportDto,
  PurchaseNormsConfigDto,
  PurchaseNormsUpdate,
  PurchasePlanReportDto,
  ReceivablesReportDto,
  RejectClientInput,
  ReturnsReportDto,
  SaveLayoutInput,
  SaveMoyskladConfigInput,
  ShelfLifeConfigDto,
  ShrinkageNormsConfigDto,
  ShrinkageReportDto,
  SuppliersReportDto,
  StaffCredentialsInput,
  StaffDto,
  StaffLoginInput,
  StaffQuery,
  SyncLogDto,
  SyncStatusDto,
  TelegramChatDto,
  TelegramTokenResultDto,
  TestDigestResultDto,
  TurnoverReportDto,
  UpdateCellInput,
  UpdateClientInput,
  UpdateStaffInput,
  WarehouseCellDto,
  WarehouseLayoutDto,
} from '@riba/shared';
import { api } from './http';

/** Typed calls, one per backend endpoint. Types come from @riba/shared. */
export const endpoints = {
  auth: {
    staffLogin: (input: StaffLoginInput) =>
      api<LoginResponse>('/auth/staff/login', { method: 'POST', body: input, anonymous: true }),
  },

  clients: {
    list: (query: Partial<ClientsQuery>) =>
      api<Paginated<ClientDto>>('/admin/users', { query: query as Record<string, string> }),

    get: (id: string) => api<ClientDto>(`/admin/users/${id}`),

    approve: (id: string, input: ApproveClientInput) =>
      api<ClientDto>(`/admin/users/${id}/approve`, { method: 'POST', body: input }),

    reject: (id: string, input: RejectClientInput) =>
      api<ClientDto>(`/admin/users/${id}/reject`, { method: 'POST', body: input }),

    update: (id: string, input: UpdateClientInput) =>
      api<ClientDto>(`/admin/users/${id}`, { method: 'PATCH', body: input }),

    changeCredentials: (id: string, input: ChangeCredentialsInput) =>
      api<ClientDto>(`/admin/users/${id}/credentials`, { method: 'PATCH', body: input }),

    bulkCreate: (names: string[]) =>
      api<BulkCreatedClient[]>('/admin/users/bulk', { method: 'POST', body: { names } }),

    bulkAssignDriver: (userIds: string[], driverId: string | null) =>
      api<{ count: number }>('/admin/users/bulk/driver', {
        method: 'POST',
        body: { userIds, driverId },
      }),

    bulkPriceVisibility: (userIds: string[], canViewPrices: boolean) =>
      api<{ count: number }>('/admin/users/bulk/price-visibility', {
        method: 'POST',
        body: { userIds, canViewPrices },
      }),

    priceTypes: () => api<PriceTypeDto[]>('/admin/users/price-types'),

    bulkPriceType: (userIds: string[], priceTypeId: string | null) =>
      api<{ count: number }>('/admin/users/bulk/price-type', {
        method: 'POST',
        body: { userIds, priceTypeId },
      }),

    bulkPassword: (userIds: string[], newPassword: string) =>
      api<{ count: number }>('/admin/users/bulk/password', {
        method: 'POST',
        body: { userIds, newPassword },
      }),

    bulkDelete: (userIds: string[]) =>
      api<{ count: number }>('/admin/users/bulk', { method: 'DELETE', body: { userIds } }),
  },

  staff: {
    list: (query: StaffQuery = {}) =>
      api<StaffDto[]>('/admin/staff', { query: query as Record<string, string> }),

    create: (input: CreateStaffInput) =>
      api<StaffDto>('/admin/staff', { method: 'POST', body: input }),

    update: (id: string, input: UpdateStaffInput) =>
      api<StaffDto>(`/admin/staff/${id}`, { method: 'PATCH', body: input }),

    setCredentials: (id: string, input: StaffCredentialsInput) =>
      api<StaffDto>(`/admin/staff/${id}/credentials`, { method: 'POST', body: input }),

    remove: (id: string) => api<{ ok: true }>(`/admin/staff/${id}`, { method: 'DELETE' }),
  },

  orders: {
    list: (query: Partial<OrdersQuery>) =>
      api<Paginated<OrderDto>>('/admin/orders', { query: query as Record<string, string> }),

    get: (id: string) => api<OrderDto>(`/admin/orders/${id}`),

    retryPush: (id: string) => api<OrderDto>(`/admin/orders/${id}/retry-push`, { method: 'POST' }),

    refreshState: (id: string) =>
      api<OrderDto>(`/admin/orders/${id}/refresh-state`, { method: 'POST' }),

    remove: (id: string) => api<void>(`/admin/orders/${id}`, { method: 'DELETE' }),
  },

  shipments: {
    list: (query: {
      page?: number;
      pageSize?: number;
      status?: DeliveryStatus;
      driverId?: string;
    }) => api<Paginated<DeliveryDto>>('/admin/shipments', { query }),
  },

  catalog: {
    products: (query: Partial<ProductsQuery>) =>
      api<Paginated<AdminProductDto>>('/admin/catalog/products', {
        query: query as Record<string, string>,
      }),
    categories: () => api<CategoryDto[]>('/admin/catalog/categories'),
    setProductCell: (productId: string, cellId: string | null) =>
      api<ProductCellDto>(`/admin/products/${productId}/cell`, {
        method: 'PUT',
        body: { cellId },
      }),
  },

  cells: {
    list: (includeInactive = false) =>
      api<CellsListDto>('/admin/cells', {
        query: includeInactive ? { includeInactive: 'true' } : {},
      }),
    create: (input: CreateCellInput) =>
      api<WarehouseCellDto>('/admin/cells', { method: 'POST', body: input }),
    update: (id: string, input: UpdateCellInput) =>
      api<WarehouseCellDto>(`/admin/cells/${id}`, { method: 'PATCH', body: input }),
    remove: (id: string) => api<void>(`/admin/cells/${id}`, { method: 'DELETE' }),
    saveOrder: (cellIds: string[]) =>
      api<CellsListDto>('/admin/cells/order', { method: 'PUT', body: { cellIds } }),
  },

  warehouse: {
    layout: () => api<WarehouseLayoutDto>('/admin/warehouse/layout'),
    /** Whole-geometry save — sections missing from the payload are DELETED. */
    saveLayout: (input: SaveLayoutInput) =>
      api<WarehouseLayoutDto>('/admin/warehouse/layout', { method: 'PUT', body: input }),
    /** Immediate cell↔spot assignment; `cellId: null` clears the spot. */
    assignSpot: (input: AssignSpotInput) =>
      api<WarehouseLayoutDto>('/admin/warehouse/spot', { method: 'PUT', body: input }),
  },

  sync: {
    triggerFull: () => api<{ started: boolean }>('/admin/sync/full', { method: 'POST' }),
    status: () => api<SyncStatusDto>('/admin/sync/status'),
    history: (limit = 20) => api<SyncLogDto[]>('/admin/sync/history', { query: { limit } }),
    counts: () => api<CatalogCountsDto>('/admin/sync/counts'),
  },

  settings: {
    get: () => api<MoyskladSettingsDto>('/admin/settings/moysklad'),
    setToken: (token: string) =>
      api<MoyskladSettingsDto>('/admin/settings/moysklad/token', {
        method: 'PUT',
        body: { token },
      }),
    test: () => api<ConnectionTestDto>('/admin/settings/moysklad/test', { method: 'POST' }),
    options: () => api<MoyskladOptionsDto>('/admin/settings/moysklad/options'),
    saveConfig: (input: SaveMoyskladConfigInput) =>
      api<MoyskladSettingsDto>('/admin/settings/moysklad', { method: 'PUT', body: input }),
    setCheckStock: (checkStock: boolean) =>
      api<MoyskladSettingsDto>('/admin/settings/moysklad/check-stock', {
        method: 'PATCH',
        body: { checkStock },
      }),
    setHideInactiveProducts: (hideInactiveProducts: boolean) =>
      api<MoyskladSettingsDto>('/admin/settings/moysklad/hide-inactive-products', {
        method: 'PATCH',
        body: { hideInactiveProducts },
      }),
    pickCategories: () => api<PickCategoriesDto>('/admin/settings/moysklad/pick-categories'),
    savePickCategories: (order: string[]) =>
      api<PickCategoriesDto>('/admin/settings/moysklad/pick-categories', {
        method: 'PUT',
        body: { order },
      }),
    webhooks: () => api<MoyskladWebhooksStatusDto>('/admin/settings/moysklad/webhooks'),
    registerWebhooks: () =>
      api<MoyskladWebhooksStatusDto>('/admin/settings/moysklad/webhooks', { method: 'POST' }),
  },

  notifications: {
    get: () => api<NotificationSettingsDto>('/admin/settings/notifications'),
    setToken: (token: string) =>
      api<TelegramTokenResultDto>('/admin/settings/notifications/token', {
        method: 'PUT',
        body: { token },
      }),
    saveConfig: (input: NotificationSettingsUpdate) =>
      api<NotificationSettingsDto>('/admin/settings/notifications', {
        method: 'PUT',
        body: input,
      }),
    chats: () => api<TelegramChatDto[]>('/admin/settings/notifications/chats', { method: 'POST' }),
    test: () => api<TestDigestResultDto>('/admin/settings/notifications/test', { method: 'POST' }),
  },

  counterparties: {
    search: (q: string) => api<CounterpartyDto[]>('/admin/counterparties/search', { query: { q } }),
  },

  /** Sklad → POS bridge: own-shop отгрузки pushed to the VkusMore POS. */
  pos: {
    get: () => api<PosBridgeSettingsDto>('/admin/settings/pos'),
    saveConfig: (input: PosBridgeConfigUpdate) =>
      api<PosBridgeSettingsDto>('/admin/settings/pos', { method: 'PUT', body: input }),
    setSecret: (secret: string) =>
      api<PosBridgeSecretResultDto>('/admin/settings/pos/secret', {
        method: 'PUT',
        body: { secret },
      }),
    outbox: () => api<PosOutboxRowDto[]>('/admin/settings/pos/outbox'),
    retry: (id: string) =>
      api<PosOutboxRowDto>(`/admin/settings/pos/outbox/${id}/retry`, { method: 'POST' }),
  },

  audit: {
    list: (query: Partial<AuditQuery>) =>
      api<Paginated<AuditEntryDto>>('/admin/audit', { query: query as Record<string, string> }),
  },

  reports: {
    receivables: (query: { windowDays?: number; refresh?: string }) =>
      api<ReceivablesReportDto>('/admin/reports/receivables', { query }),
    dailyOrders: (query: { date?: string; refresh?: string }) =>
      api<DailyOrdersReportDto>('/admin/reports/daily-orders', { query }),
    deadStock: (query: { minDays?: number }) =>
      api<DeadStockReportDto>('/admin/reports/dead-stock', { query }),
    profit: (query: {
      by: 'product' | 'counterparty';
      dateFrom: string;
      dateTo: string;
      refresh?: string;
    }) => api<ProfitReportDto>('/admin/reports/profit', { query }),
    turnover: (query: { dateFrom: string; dateTo: string; refresh?: string }) =>
      api<TurnoverReportDto>('/admin/reports/turnover', { query }),
    expiry: (query: { windowDays?: number; refresh?: string }) =>
      api<ExpiryReportDto>('/admin/reports/expiry', { query }),
    shelfLife: () => api<ShelfLifeConfigDto>('/admin/reports/shelf-life'),
    setCategoryShelfLife: (categoryId: string, days: number | null) =>
      api<{ ok: true }>(`/admin/reports/shelf-life/category/${categoryId}`, {
        method: 'PUT',
        body: { days },
      }),
    setProductShelfLife: (productId: string, days: number | null) =>
      api<{ ok: true }>(`/admin/reports/shelf-life/product/${productId}`, {
        method: 'PUT',
        body: { days },
      }),
    purchasePlan: (query: { windowDays?: number; refresh?: string }) =>
      api<PurchasePlanReportDto>('/admin/reports/purchase-plan', { query }),
    purchaseNorms: () => api<PurchaseNormsConfigDto>('/admin/reports/purchase-norms'),
    setCategoryPurchaseNorms: (categoryId: string, norms: PurchaseNormsUpdate) =>
      api<{ ok: true }>(`/admin/reports/purchase-norms/category/${categoryId}`, {
        method: 'PUT',
        body: norms,
      }),
    abcXyz: (query: { weeks?: number; refresh?: string }) =>
      api<AbcXyzReportDto>('/admin/reports/abc-xyz', { query }),
    shrinkage: (query: { dateFrom: string; dateTo: string; refresh?: string }) =>
      api<ShrinkageReportDto>('/admin/reports/shrinkage', { query }),
    shrinkageNorms: () => api<ShrinkageNormsConfigDto>('/admin/reports/shrinkage-norms'),
    setCategoryShrinkageNorm: (categoryId: string, pct: number | null) =>
      api<{ ok: true }>(`/admin/reports/shrinkage-norms/category/${categoryId}`, {
        method: 'PUT',
        body: { pct },
      }),
    returns: (query: { dateFrom: string; dateTo: string; refresh?: string }) =>
      api<ReturnsReportDto>('/admin/reports/returns', { query }),
    suppliers: (query: { dateFrom: string; dateTo: string; refresh?: string }) =>
      api<SuppliersReportDto>('/admin/reports/suppliers', { query }),
    inventory: (query: { dateFrom: string; dateTo: string; refresh?: string }) =>
      api<InventoryReportDto>('/admin/reports/inventory', { query }),
  },
};
